import { useState, useMemo } from "react";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { T, SERIF, SECTIONS, computeModel, ALL_Q } from "../lib/model";

const { FiSettings, FiActivity, FiSearch, FiArrowRight, FiCheckCircle, FiAlertTriangle, FiTarget, FiInfo } = FiIcons;

export default function AdminSimulator({ onBack }) {
  const [simAnswers, setSimAnswers] = useState({});
  const [activeTab, setActiveTab] = useState("exposure");

  const model = useMemo(() => computeModel(simAnswers), [simAnswers]);

  const setAns = (qid, idx) => {
    setSimAnswers(prev => ({ ...prev, [qid]: idx }));
  };

  const reset = () => setSimAnswers({});

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
      <header className="bg-[#0D2440] text-white px-8 py-4 flex justify-between items-center shadow-xl">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-teal-500 rounded-xl">
            <SafeIcon icon={FiSettings} className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-black text-xl tracking-tight">Scoring Engine Simulator</h1>
            <p className="text-[10px] uppercase font-bold text-teal-400 tracking-widest">Internal Administrator Tool v3.0 (Qualitative Shift)</p>
          </div>
        </div>
        <button 
          onClick={onBack}
          className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-xl text-sm font-black transition-all"
        >
          Exit Simulator
        </button>
      </header>

      <main className="flex-1 grid lg:grid-cols-[1fr_450px] gap-8 p-8 max-w-[1600px] mx-auto w-full">
        {/* Input Panel */}
        <div className="space-y-8 overflow-y-auto pr-4" style={{ maxHeight: "calc(100vh - 160px)" }}>
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black text-[#0D2440]">Input Vector</h2>
              <button onClick={reset} className="text-sm font-bold text-red-500 hover:underline">Reset All</button>
            </div>

            <div className="space-y-12">
              {SECTIONS.map(s => (
                <div key={s.id} className="space-y-6">
                  <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                    <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-xs font-black">{s.weight}%</div>
                    <h3 className="font-black text-gray-900">{s.name}</h3>
                  </div>
                  <div className="grid gap-6">
                    {s.questions.map(q => (
                      <div key={q.id} className="space-y-3">
                        <label className="text-sm font-bold text-gray-600 block">{q.text}</label>
                        <div className="flex flex-wrap gap-2">
                          {q.opts.map(([label], idx) => (
                            <button
                              key={idx}
                              onClick={() => setAns(q.id, idx)}
                              className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                                simAnswers[q.id] === idx 
                                  ? 'bg-[#0D2440] text-white' 
                                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Output Panel */}
        <div className="space-y-6">
          <div className="bg-[#0D2440] rounded-[40px] p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full -mr-32 -mt-32 blur-3xl" />
            
            <div className="relative z-10 space-y-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-3xl p-6 border border-white/10">
                  <div className="text-[10px] font-black uppercase text-teal-400 tracking-widest mb-2">Exposure Score (Internal)</div>
                  <div className="text-5xl font-black">{model.score}<span className="text-white/30 text-2xl">/100</span></div>
                  <div className="mt-2 text-xs font-bold text-teal-200">{model.tier.name} Risk Band</div>
                </div>
                <div className="bg-white/5 rounded-3xl p-6 border border-white/10">
                  <div className="text-[10px] font-black uppercase text-lime-400 tracking-widest mb-2">Maturity Level</div>
                  <div className="text-5xl font-black">{model.maturity}<span className="text-white/30 text-2xl">/5</span></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-3xl p-6 border border-white/10">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-[10px] font-black uppercase text-blue-400 tracking-widest">Evidence Confidence</div>
                  <div className="text-2xl font-black">{model.confidence}%</div>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-400 transition-all duration-700"
                    style={{ width: `${model.confidence}%` }}
                  />
                </div>
                <div className="mt-2 text-[10px] font-bold text-blue-200">{model.confidenceNotice} Notice applied</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[40px] p-8 shadow-xl border border-gray-100 h-full">
            <div className="flex gap-2 mb-8 bg-gray-50 p-1.5 rounded-2xl overflow-x-auto">
              {['exposure', 'maturity', 'attack', 'identity'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 min-w-[80px] py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === tab ? 'bg-white text-[#0D2440] shadow-sm' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="space-y-6">
              {activeTab === 'exposure' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Domain Contribution</h4>
                  {model.dims.map(d => (
                    <div key={d.name} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-black text-gray-700">{d.name}</span>
                        <span className="text-sm font-black text-[#0D2440]">{d.pct !== null ? Math.round(d.pct * 100) : '—'}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-500 transition-all duration-500"
                          style={{ width: `${d.pct !== null ? d.pct * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'attack' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Generated Attack Paths</h4>
                  {model.attackPaths.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No paths generated from current inputs.</p>
                  ) : (
                    model.attackPaths.map((ap, i) => (
                      <div key={i} className="p-4 bg-red-50 rounded-2xl border border-red-100 space-y-2">
                        <div className="text-[10px] font-black text-red-700">{ap.reason}</div>
                        <div className="text-[11px] font-bold text-gray-600">
                          <span className="text-red-600">Sequence:</span> {ap.trigger} &rarr; {ap.failure} &rarr; {ap.event} &rarr; {ap.impact}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'identity' && (
                <div className="space-y-4">
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Assessment Identity</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-2xl">
                      <div className="text-[9px] font-black uppercase text-gray-400 mb-1">ID</div>
                      <div className="text-xs font-black text-gray-700">{model.identity.id}</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-2xl">
                      <div className="text-[9px] font-black uppercase text-gray-400 mb-1">Status</div>
                      <div className="text-xs font-black text-gray-700">{model.identity.status}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}